import { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ChevronLeft, ChevronRight, Lock, Headphones } from "lucide-react";
import { api, fileUrl, useAuth } from "../lib/api";
import { NarrationPlayer } from "../components/NarrationPlayer";

export default function Reader() {
  const { id } = useParams();
  const { user } = useAuth();
  const [ch, setCh] = useState(null);
  const [narrate, setNarrate] = useState(false);
  const refs = useRef([]);
  useEffect(() => {
    setCh(null);
    api.get(`/chapters/${id}`).then((r) => { setCh(r.data); if (user && !r.data.locked) api.post(`/chapters/${id}/read`).catch(() => {}); });
    window.scrollTo(0, 0);
  }, [id, user]);
  if (!ch) return <div className="p-10 jx-muted text-center">Loading chapter…</div>;
  const idx = ch.chapters.findIndex((x) => x.id === ch.id);
  const prev = ch.chapters[idx - 1], next = ch.chapters[idx + 1];
  return (
    <main className="max-w-2xl mx-auto pb-32">
      <div className="px-4 py-3 flex items-center justify-between">
        <Link to={`/comic/${ch.comic.id}`} data-testid="reader-back" className="text-sm jx-muted flex items-center gap-1"><ChevronLeft size={16} />{ch.comic.title}</Link>
        <span className="font-mono text-xs jx-muted">CH {ch.number}</span>
      </div>
      {ch.comic.ai_generated && <div className="mx-4 mb-2 jx-tag jx-violet">AI-generated content</div>}
      <div className="px-4">
        {!narrate ? <button data-testid="enable-narration-btn" onClick={() => user ? setNarrate(true) : (window.location.href = "/login")} className="w-full jx-surface rounded-2xl py-3 flex items-center justify-center gap-2 text-sm font-semibold hover:border-[var(--jx-mint)]"><Headphones size={16} className="jx-mint" />Read aloud with AI voices</button>
          : <NarrationPlayer chapter={ch} onPanel={(p) => refs.current[p]?.scrollIntoView({ behavior: "smooth", block: "center" })} />}
      </div>
      <div data-testid="reader-panels" className="mt-4">
        {ch.panels.map((p, i) => (
          <figure key={i} ref={(el) => (refs.current[i] = el)} data-testid={`panel-${i}`} className="relative">
            {p.image_url ? <img src={fileUrl(p.image_url)} alt={p.description || `Panel ${i + 1}`} loading="lazy" className="w-full block" /> : <div className="aspect-[4/5] jx-halftone grid place-items-center jx-muted text-sm px-8 text-center">{p.description}</div>}
            {(p.narration || p.dialogue.length > 0) && <figcaption className="px-4 py-4 space-y-2 bg-[var(--jx-bg2)]">
              {p.narration && <p className="text-sm italic jx-muted border-l-2 border-[var(--jx-gold)] pl-3">{p.narration}</p>}
              {p.dialogue.map((d, j) => <p key={j} className="text-sm"><span className="font-bold jx-pink">{d.character}:</span> {d.text}</p>)}
            </figcaption>}
          </figure>
        ))}
      </div>
      {ch.locked && <div data-testid="chapter-locked" className="m-4 jx-surface jx-ink rounded-2xl p-6 text-center">
        <Lock className="mx-auto jx-gold" /><div className="font-display text-xl font-bold mt-2">Unlock the full chapter</div>
        <p className="text-sm jx-muted mt-1">This is a premium comic. Purchase it once to read every chapter.</p>
        <Link to={`/comic/${ch.comic.id}`} data-testid="unlock-btn" className="jx-btn inline-block px-6 py-3 mt-4">View purchase options</Link>
      </div>}
      <div className="flex justify-between px-4 mt-6">
        {prev ? <Link to={`/read/${prev.id}`} data-testid="prev-chapter-btn" className="px-5 py-3 rounded-full jx-surface flex items-center gap-1"><ChevronLeft size={16} />Ch {prev.number}</Link> : <span />}
        {next && <Link to={`/read/${next.id}`} data-testid="next-chapter-btn" className="jx-btn px-5 py-3 flex items-center gap-1">Ch {next.number}<ChevronRight size={16} /></Link>}
      </div>
    </main>
  );
}
